import React, { useState, useEffect, useMemo, useCallback, useReducer } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  CardContent,
  Typography,
  Divider,
  TableSortLabel,
  TextField,
  useTheme,
  Grid,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";
import Icons from "../../Utils/Icons";
import { GET, DELETE } from "../Generic/CrudRequest";
import "./CheckList.css";
import ModalGeneric from "../Generic/ModalGeneric";
import useExcelExport from "../../Utils/useExcelExport";
import { SaveAlt } from "@mui/icons-material";
import { searchPermissions } from "../Users/PermissionsUser/PermissionsActions";
import { useTranslation } from "react-i18next";

const initialState = {
  page: 0,
  rowsPerPage: 10,
  rows: [],
  orderBy: "",
  column: 0,
  order: "asc",
  totalRecords: 0,
  searchText: "",
  estatus: true,
  isModalOpen: false,
  modalTitle: "",
  modalDescription: "",
  checkListIdToDelete: null,
  bodyModal: null,
  caseModal: 0,
};

function checkListReducer(state, action) {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0 };
    case 'SET_ROWS':
      return { ...state, rows: action.payload };
    case 'SET_TOTAL_RECORDS':
      return { ...state, totalRecords: action.payload };
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: action.payload };
    case 'SET_ESTATUS':
      return { ...state, estatus: action.payload };
    case 'SET_SORT':
      return {
        ...state,
        orderBy: action.payload.orderBy,
        column: action.payload.column,
        order: action.payload.order,
      };
    case 'SET_MODAL':
      return {
        ...state,
        isModalOpen: action.payload.isOpen,
        modalTitle: action.payload.title || "",
        modalDescription: action.payload.description || "",
        checkListIdToDelete: action.payload.id || null,
        bodyModal: action.payload.body || null,
        caseModal: action.payload.case || 0,
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        searchText: "",
        estatus: true,
      };
    default:
      return state;
  }
}


const SearchFilters = React.memo(({ state, dispatch, handleSearch, handleClear, theme }) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={3}>
        <TextField
          label={t("checkList.name")}
          type="search"
          InputLabelProps={{ shrink: true }}
          variant="standard"
          size="small"
          value={state.searchText}
          onChange={(e) => dispatch({ type: 'SET_SEARCH_TEXT', payload: e.target.value })}
          sx={{
            width: 400,
            maxWidth: "100%",
            color: theme.palette.primary.main,
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          select
          label={t("units.status")}
          InputLabelProps={{ shrink: true }}
          name="estatus"
          variant="standard"
          size="small"
          value={state.estatus}
          fullWidth
          onChange={(e) => dispatch({ type: 'SET_ESTATUS', payload: e.target.value })}
        >
          <MenuItem value={true}>{t("units.active")}</MenuItem>
          <MenuItem value={false}>{t("units.inactive")}</MenuItem>
        </TextField>
      </Grid>
      <Icons
        iconName="Search"
        fontSize="large"
        onClick={handleSearch}
        style={{ color: theme.palette.primary.main }}
        tooltip={t("buttons.search")}
      />
      <Icons
        iconName="Clear"
        fontSize="large"
        onClick={handleClear}
        style={{ color: theme.palette.primary.main }}
        tooltip={t("buttons.clear")}
      />
    </Grid>
  );
});

const TableHeader = React.memo(({ columns, state, handleRequestSort }) => {
  const { t } = useTranslation();
  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align="left"
            style={{ minWidth: column.minWidth, fontWeight: "bold" }}
          >
            <TableSortLabel
              active={state.orderBy === column.id}
              direction={state.orderBy === column.id ? state.order : "asc"}
              onClick={() => handleRequestSort(column.id, columns.indexOf(column))}
            >
              {t(column.label)}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell />
        <TableCell />
      </TableRow>
    </TableHead>
  );
});

const CheckList = ({ config }) => {
  const [state, dispatch] = useReducer(checkListReducer, initialState);
  const theme = useTheme();
  const exportToExcel = useExcelExport();
  const [permissions, setPermissions] = useState({
    register: false,
    edit: false,
    eliminated: false,
  });
  const { t, i18n } = useTranslation();

  let desc = i18n.language === "en" ? "descripcionIngles" : "descripcion"
  let descTxt = i18n.language === "en" ? "descripcionTextoIngles" : "descripcionTexto"
  const columns = [
    { id: desc, label: "checkList.nameGeneric", minWidth: "100px" },
    { id: descTxt, label: "checkList.descGeneric", minWidth: "100px" },
    {
      id: "checkListInspeccionGeneralTipos",
      label: "checkList.inspectionTypes",
      minWidth: "100px",
    },
  ];

  const fetchCheckList = useCallback(async () => {
    try {
      const dataCheckList = await GET(`${config.url}/api/Checklist/Paginado`, {
        start: state.page * state.rowsPerPage,
        length: state.rowsPerPage,
        descripcion: state.searchText,
        status: state.estatus,
        column: state.column,
        dir: state.order,
      });

      dispatch({ type: 'SET_ROWS', payload: dataCheckList.results });
      dispatch({ type: 'SET_TOTAL_RECORDS', payload: dataCheckList.rowCount });

      const permissionsArray = JSON.parse(localStorage.getItem("permissions"));
      setPermissions({
        register: searchPermissions("CheckList", "Registrar", permissionsArray),
        edit: searchPermissions("CheckList", "Editar", permissionsArray),
        eliminated: searchPermissions("CheckList", "Eliminar", permissionsArray),
      });
    } catch (error) {
      console.error('Error al cargar los datos:', error);
      // Aquí podrías implementar un manejo de errores más robusto
    }
  }, [state.page, state.rowsPerPage, state.searchText, state.estatus, state.column, state.order, config.url]);

  useEffect(() => {
    fetchCheckList();
  }, [fetchCheckList]);

  const handleDeleteCheckList = useCallback(async (id) => {
    try {
      await DELETE(`${config.url}/api/Checklist/${id}`);
      fetchCheckList();
    } catch (error) {
      console.error('Error al eliminar el CheckList:', error);
      // Aquí podrías implementar un manejo de errores más robusto
    }
  }, [config.url, fetchCheckList]);

  const handleExport = useCallback(() => {
    exportToExcel(state.rows, columns, "CHECKLIST.xlsx");
  }, [state.rows, exportToExcel]);

  const renderInspectionTypes = useMemo(() => (types) => {
    if (!types || types.length === 0) return <i>No Disponibles</i>;
    return (
      <List>
        {types.map((item, index) => (
          <ListItem key={index}>
            <ListItemText>
              <Typography variant="body1">
                {`${index + 1}. ${item.idInspeccionGeneralTipoNavigation.descripcion}`}
              </Typography>
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  }, []);

  const handleChangePage = (event, newPage) => {
    dispatch({ type: 'SET_PAGE', payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({ type: 'SET_ROWS_PER_PAGE', payload: +event.target.value });
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = state.orderBy === property && state.order === "asc";
    dispatch({ type: 'SET_SORT', payload: { orderBy: property, column: columnNumber, order: isAsc ? "desc" : "asc" } });
  };

  const handleSearch = () => {
    fetchCheckList();
  };

  const handleClear = () => {
    dispatch({ type: 'RESET_FILTERS' });
    fetchCheckList();
  };

  const handleDeleteCheckListModal = (userName, id) => {
    console.log("userName", userName);
    console.log("id", id);
    dispatch({
      type: 'SET_MODAL',
      payload: {
        isOpen: true,
        title: t("checkList.deleteConfirm.title"),
        description: t("checkList.deleteConfirm.message", { name: userName }),
        id: id,
        case: 1
      }
    });
  };

  const handleCloseModal = () => {
    dispatch({ type: 'SET_MODAL', payload: { isOpen: false } });
  };

  const handleAcceptModal = () => {
    if (state.caseModal === 1) {
      handleDeleteCheckList(state.checkListIdToDelete);
    }
    handleCloseModal();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      {state.isModalOpen && (
        <ModalGeneric
          open={state.isModalOpen}
          title={state.modalTitle}
          description={state.modalDescription}
          onClose={handleCloseModal}
          onAccept={handleAcceptModal}
          bodyComponent={state.bodyModal}
        />
      )}
      <CardContent>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-between",
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {t("checkList.title")}
          </Typography>
          {permissions.register && (
            <Icons
              iconName="Add"
              fontSize="large"
              to="/Checklist/Agregar"
              style={{
                marginLeft: "auto",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.white.main,
              }}
              tooltip={t("buttons.add")}
            />
          )}
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            alignItems: "auto",
          }}
        >
          <SearchFilters
            state={state}
            dispatch={dispatch}
            handleSearch={handleSearch}
            handleClear={handleClear}
            theme={theme}
          />
        </CardContent>
        <Paper
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            overflow: "hidden",
            boxShadow: "none",
            border: "none",
          }}
        >
          <TableContainer>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={handleExport}
            >
              {t("buttons.exportExcel")}
            </Button>
            <Table stickyHeader aria-label="sticky table">
              <TableHeader
                columns={columns}
                state={state}
                handleRequestSort={handleRequestSort}
              />
              <TableBody>
                {state.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      {t("checkList.noCheckListsAvailable")}
                    </TableCell>
                  </TableRow>
                )}
                {state.rows.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align="left">
                          {column.id === "checkListInspeccionGeneralTipos" ? (
                            row[column.id] !== null && row[column.id] !== "" ? (
                              renderInspectionTypes(row[column.id])
                            ) : (
                              <i>{t("checkList.notAvailable")}</i>
                            )
                          ) : row[column.id] !== null &&
                            row[column.id] !== "" ? (
                            row[column.id]
                          ) : (
                            <i>{t("checkList.notAvailable")}</i>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {permissions.edit && (
                          <Icons
                            iconName="Edit"
                            fontSize="medium"
                            style={{
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff",
                              marginLeft: 1,
                            }}
                            to={`/Checklist/Editar/${row.idCheckList}`}
                            tooltip={t("buttons.edit")}
                          />
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {permissions.eliminated && (
                          <Icons
                            onClick={() =>
                              handleDeleteCheckListModal(
                                row[desc],
                                row.idCheckList
                              )
                            }
                            iconName="Delete"
                            fontSize="medium"
                            style={{
                              backgroundColor: theme.palette.danger.main,
                              color: "#fff",
                              marginLeft: 1,
                            }}
                            tooltip={t("buttons.delete")}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100, { value: -1, label: t("pagination.all") }]}
            component="div"
            count={state.totalRecords}
            rowsPerPage={state.rowsPerPage}
            page={state.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("pagination.labelRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.displayedRows", { from, to, count })
            }
          />
        </Paper>
      </CardContent>
    </Card>
  );
};

export default React.memo(CheckList);
